import { useEffect, useMemo, useState } from "react";
import { PublicLayout } from "@/components/public/PublicLayout";
import { SEO } from "@/components/SEO";
import { publicApi } from "@/lib/api";
import { useBranding } from "@/contexts/BrandingContext";

const norm = (s) => (s || "").toLocaleLowerCase("tr-TR").trim();

const StandingsTable = ({ rows, title, subtitle, testid, ownNames }) => (
    <div className="bg-liv-card border border-liv-border overflow-x-auto">
        <div className="px-5 md:px-6 py-4 border-b border-liv-border flex items-baseline justify-between gap-4">
            <h2 className="font-display text-2xl md:text-3xl uppercase tracking-wide" data-testid={`${testid}-title`}>{title}</h2>
            {subtitle ? <span className="text-xs md:text-sm text-neutral-400">{subtitle}</span> : null}
        </div>
        <table className="w-full text-sm" data-testid={testid}>
            <thead className="text-neutral-500 text-xs uppercase tracking-wider bg-liv-surface">
                <tr>
                    <th className="text-left px-4 py-4">#</th>
                    <th className="text-left py-4">Takım</th>
                    <th className="text-center py-4">O</th>
                    <th className="text-center py-4">G</th>
                    <th className="text-center py-4">B</th>
                    <th className="text-center py-4">M</th>
                    <th className="text-center py-4">AG</th>
                    <th className="text-center py-4">YG</th>
                    <th className="text-center py-4">Av</th>
                    <th className="text-center py-4 px-4 font-bold text-liv-yellow">P</th>
                </tr>
            </thead>
            <tbody>
                {rows.map((r) => {
                    const isOwn = ownNames.includes(norm(r.team_name));
                    return (
                        <tr key={r.id} className={`border-t border-liv-border ${isOwn ? "bg-liv-yellow/10 text-liv-yellow font-bold" : ""}`} data-testid={`${testid}-row-${r.rank}`}>
                            <td className="px-4 py-3 tabular-nums">{r.rank}</td>
                            <td className="py-3 font-semibold">{r.team_name}</td>
                            <td className="text-center py-3 tabular-nums">{r.played}</td>
                            <td className="text-center py-3 tabular-nums">{r.wins}</td>
                            <td className="text-center py-3 tabular-nums">{r.draws}</td>
                            <td className="text-center py-3 tabular-nums">{r.losses}</td>
                            <td className="text-center py-3 tabular-nums">{r.goals_for}</td>
                            <td className="text-center py-3 tabular-nums">{r.goals_against}</td>
                            <td className="text-center py-3 tabular-nums">{r.goal_difference}</td>
                            <td className="text-center py-3 px-4 tabular-nums font-bold">{r.points}</td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    </div>
);

const Standings = () => {
    const [rows, setRows] = useState([]);
    const { branding } = useBranding();
    useEffect(() => { publicApi.standings().then(setRows); }, []);

    const ownNames = useMemo(
        () => [norm(branding.name), norm(branding.short_name)].filter(Boolean),
        [branding.name, branding.short_name],
    );

    // Group dynamically by the league/group label coming from the sync source, so that
    // a new league or group shows up without any code change.
    const groups = useMemo(() => {
        const map = new Map();
        rows.forEach((r) => {
            const key = r.group_label || r.league_group || "Puan Durumu";
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(r);
        });
        return [...map.entries()].map(([label, items]) => ({
            label,
            rows: [...items].sort((a, b) => (a.rank || 0) - (b.rank || 0)),
        }));
    }, [rows]);

    const season = rows[0]?.season ? rows[0].season.replace("-", "/") : "";

    return (
        <PublicLayout>
            <SEO title={`Puan Durumu · ${branding.name}`} description={`${branding.name} güncel lig puan durumu.`} />
            <div className="container-x section-pad">
                {season ? <div className="overline" data-testid="standings-season">{season} Sezonu</div> : null}
                <h1 className="font-display text-6xl md:text-8xl uppercase mt-2">Puan Durumu</h1>
                <p className="text-neutral-400 mt-3 max-w-2xl text-sm md:text-base">
                    {groups[0]?.label ? `${groups[0].label} güncel sıralaması.` : "Lig sıralaması."} Veriler resmi maç sonuçlarından alınmıştır.
                </p>

                <div className="mt-10 space-y-10">
                    {groups.map((g, i) => (
                        <StandingsTable
                            key={g.label}
                            rows={g.rows}
                            title={g.label}
                            subtitle={`${g.rows.length} takım`}
                            testid={`standings-group-${i}`}
                            ownNames={ownNames}
                        />
                    ))}
                    {groups.length === 0 && (
                        <div className="text-neutral-500 text-sm" data-testid="standings-empty">Puan durumu verisi henüz yüklenmedi.</div>
                    )}
                </div>
            </div>
        </PublicLayout>
    );
};
export default Standings;
